import { useState, useEffect, useCallback } from "react";
import { Row, Col, Input, Select, Table, Typography, DatePicker } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchVendorOrders } from "services/ordersAndBookings.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import {
  getOrderColumns,
  ORDER_STATUS_OPTIONS,
  OrderItemsExpanded,
} from "../commons/orders.helpers";

const { Text } = Typography;

const ORDER_PREFIX = "ORD-";

const SEARCH_TYPE_OPTIONS = [
  { value: "order_number", label: "Order #" },
  { value: "customer_id", label: "Phone No." },
];

function buildApiValue(type, value) {
  if (!value) return "";
  return type === "order_number" ? `${ORDER_PREFIX}${value}` : value;
}

function OrdersTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searchType, setSearchType] = useState(searchParams.get("order_type") ?? "order_number");
  const [searchValue, setSearchValue] = useState(searchParams.get("order_q") ?? "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("order_status") ?? "all");
  const [dateRange, setDateRange]   = useState([null, null]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const syncParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (!v || v === "all" || v === "order_number") next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  }, [setSearchParams]);

  async function load(page, pageSize, status, type, value, dates) {
    setLoading(true);
    const body = { current_page: page, page_size: pageSize };
    if (status && status !== "all") body.order_status = status;
    const apiValue = buildApiValue(type, value);
    if (apiValue) body[type] = apiValue;
    if (dates?.[0]) body.from_date = dates[0].toISOString();
    if (dates?.[1]) body.to_date   = dates[1].toISOString();
    const { data, errors } = await fetchVendorOrders(body);
    setLoading(false);
    if (errors?.length) { renderErrorNotifications(errors); return; }
    setOrders(data?.orders ?? []);
    const meta = data?.meta?.pagination ?? {};
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
      total: meta.total ?? (meta.total_pages ?? 1) * pageSize,
    }));
  }

  useEffect(() => {
    load(1, 10, statusFilter, searchType, searchValue, dateRange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    let val = e.target.value;
    if (searchType === "order_number" && val.startsWith(ORDER_PREFIX)) val = val.slice(ORDER_PREFIX.length);
    setSearchValue(val);
  };

  const handleSearch = (value) => {
    const val = searchType === "order_number" && value.startsWith(ORDER_PREFIX)
      ? value.slice(ORDER_PREFIX.length) : value;
    setSearchValue(val);
    syncParams({ order_type: searchType, order_q: val, order_status: statusFilter });
    load(1, pagination.pageSize, statusFilter, searchType, val, dateRange);
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    syncParams({ order_type: type, order_q: "", order_status: statusFilter });
    if (searchValue) {
      setSearchValue("");
      load(1, pagination.pageSize, statusFilter, type, "", dateRange);
    }
  };

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    syncParams({ order_type: searchType, order_q: searchValue, order_status: status });
    load(1, pagination.pageSize, status, searchType, searchValue, dateRange);
  };

  const handleDateChange = (dates) => {
    const range = dates ?? [null, null];
    setDateRange(range);
    load(1, pagination.pageSize, statusFilter, searchType, searchValue, range);
  };

  const handleTableChange = (pag) => {
    load(pag.current, pag.pageSize, statusFilter, searchType, searchValue, dateRange);
  };

  const columns = getOrderColumns({ onView: (r) => navigate(`detail/${r.order_number}`) });

  return (
    <div className="space-y-4 py-4">
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={14} md={10}>
          <Input.Search
            addonBefore={
              <Select
                value={searchType}
                onChange={handleSearchTypeChange}
                options={SEARCH_TYPE_OPTIONS}
                style={{ width: 110 }}
              />
            }
            prefix={
              searchType === "order_number"
                ? <Text type="secondary" style={{ fontSize: 13, userSelect: "none" }}>ORD-</Text>
                : null
            }
            placeholder={searchType === "order_number" ? "100758" : "e.g. 9876543210"}
            value={searchValue}
            onChange={handleInputChange}
            onSearch={handleSearch}
            allowClear
          />
        </Col>
        <Col xs={24} sm={6} md={4}>
          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            options={ORDER_STATUS_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={10} md={8}>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={handleDateChange}
            style={{ width: "100%" }}
            placeholder={["From date", "To date"]}
            allowClear
          />
        </Col>
      </Row>

      <Table
        rowKey="order_number"
        columns={columns}
        dataSource={orders}
        loading={loading}
        size="small"
        scroll={{ x: 900 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          position: ["bottomRight"],
        }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (record) => <OrderItemsExpanded record={record} />,
          rowExpandable: (record) => (record.order_items?.length ?? 0) > 0,
        }}
      />
    </div>
  );
}

export default OrdersTab;
